import { useContext, type FC, type PropsWithChildren } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Label,
  ResponsiveContainer,
  Tooltip,
  type TooltipProps,
  XAxis,
  YAxis
} from 'recharts';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import type { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { round } from 'app/utils/number';
import TeamAvatar from 'app/components/TeamAvatar';
import ThemeContext from 'app/common/ThemeContext';
import { sort } from 'app/utils/array';
import { formatGoldLead, formatTimer } from 'app/utils/entities/match_data';
import GoldAdvantageIcon from 'app/icons/gold_advantage.svg';

import style from './style.module.scss';

const GoldAdvantageTooltip: FC<
  TooltipProps<ValueType, NameType> & { advantageColor: string; disadvantageColor: string }
> = ({ active, payload, advantageColor, disadvantageColor }) => {
  if (
    active &&
    payload &&
    payload.length &&
    typeof payload[0].value === 'number' &&
    typeof payload[1].value === 'number'
  ) {
    const goldValue = payload[0].value + payload[1].value;
    const isAdvantage = goldValue >= 0;

    return (
      <div
        className={style.goldAdvantageTooltip}
        // Match the graph area the value belongs to.
        style={{ color: isAdvantage ? advantageColor : disadvantageColor }}
      >
        <GoldAdvantageIcon className={style.goldAdvantageTooltipIcon} />
        <div>{formatGoldLead(goldValue, 'exact', false)}</div>
      </div>
    );
  }
  return null;
};

interface Props extends PropsWithChildren, ClassNameProp {
  goldAdvantage: GoldAdvantageAtTimer[];
  goldAdvantageTeamInfo: OpponentBase | undefined;
  secondTeamInfo: OpponentBase | undefined;
  goldAvantageTeam: 'radiant' | 'blue';
  display?: 'original' | 'dota-2-scoreboard';
  // Chart height (recharts ResponsiveContainer). Default 100px; pass '100%' to
  // fill a flex parent that has a definite height.
  height?: number | string;
  // Optional overrides (grad widget uses board-position colors); default to the
  // radiant/dire (or blue/red) theme tokens.
  advantageColor?: string;
  disadvantageColor?: string;
}

const GoldAdvantageChart: FC<Props> = ({
  goldAdvantage,
  goldAdvantageTeamInfo,
  secondTeamInfo,
  goldAvantageTeam,
  children,
  display = 'original',
  height = 100,
  className,
  advantageColor: advantageColorOverride,
  disadvantageColor: disadvantageColorOverride
}) => {
  const { t } = useTranslation();
  const { theme } = useContext(ThemeContext);

  const chartDataWithExplicitZeros = sort(goldAdvantage, item => item.timer).reduce((acc, item) => {
    if (acc.length === 0) {
      return [item];
    }
    const previousItem = acc[acc.length - 1];
    if ((previousItem.value > 0 && item.value < 0) || (previousItem.value < 0 && item.value > 0)) {
      const slope = (previousItem.value - item.value) / (previousItem.timer - item.timer);
      const timerAt0 = Math.abs(previousItem.value / slope) + previousItem.timer;
      return [...acc, { timer: timerAt0, value: 0 }, item];
    }
    return [...acc, item];
  }, [] as GoldAdvantageAtTimer[]);

  const chartData = chartDataWithExplicitZeros.map(({ value, timer }) => ({
    timer,
    disadvantage: Math.min(value, 0),
    advantage: Math.max(0, value)
  }));

  const advantageColor =
    advantageColorOverride ??
    (goldAvantageTeam === 'radiant' ? theme.vg_dota_radiant : theme.vg_lol_blue);
  const disadvantageColor =
    disadvantageColorOverride ??
    (goldAvantageTeam === 'radiant' ? theme.vg_dota_dire : theme.vg_lol_red);

  const originalDisplay = display === 'original';

  // Calculate minute ticks for XAxis (multiples of 5 minutes). The history can
  // start well after 00:00, so anchor the ticks on the first data point instead
  // of on zero — otherwise the chart opens on an empty stretch.
  const timers = chartData.map(item => item.timer);
  const minuteTicks: number[] = [];
  if (timers.length) {
    const minTimer = Math.min(...timers);
    const maxTimer = Math.max(...timers);
    for (let tick = Math.ceil(minTimer / 300) * 300; tick <= maxTimer; tick += 300) {
      minuteTicks.push(tick);
    }
    if (maxTimer % 300 !== 0) {
      minuteTicks.push(maxTimer); // Add the last tick if not exactly divisible by 5 minutes
    }
  }

  return (
    <div
      className={classNames(
        style.chartContainer,
        { [style.originalDisplay]: originalDisplay },
        className
      )}
    >
      {originalDisplay && (
        <h4 className={style.statsContainerTitle}>{t('component_match-recap_moba_gold-curve')}</h4>
      )}
      <div className={style.chart}>
        {originalDisplay && (
          <div className={style.overChartContainer} aria-hidden>
            <TeamAvatar withBackground team={goldAdvantageTeamInfo} />
            <TeamAvatar withBackground team={secondTeamInfo} />
          </div>
        )}

        <div className={style.overChartContainer}>{children}</div>

        <ResponsiveContainer width="99%" height={height} minWidth="0">
          <AreaChart data={chartData} margin={{ left: -30 }}>
            <YAxis
              tickFormatter={value =>
                `${Math.abs(round(value / 1000, Math.abs(value) > 9999 ? 1 : 2))}K`
              }
              tick={{ fontSize: 10 }}
              domain={[
                (dataMin: number) => Math.min(-5000, dataMin),
                (dataMax: number) => Math.max(5000, dataMax)
              ]}
              stroke={theme.component_timeline_lines}
              scale="linear"
            >
              <Label
                content={<TeamAvatar withBackground team={goldAdvantageTeamInfo} />}
                position="insideTopLeft"
              />
            </YAxis>
            <XAxis
              dataKey="timer"
              type="number"
              hide={originalDisplay}
              domain={['dataMin', 'dataMax']}
              tick={{ fontSize: 10 }}
              tickFormatter={value => formatTimer(value)}
              scale="linear"
              ticks={minuteTicks}
            />
            {!originalDisplay && (
              <Tooltip
                cursor={{ stroke: theme.global_border, strokeDasharray: '3 3' }}
                content={
                  <GoldAdvantageTooltip
                    advantageColor={advantageColor}
                    disadvantageColor={disadvantageColor}
                  />
                }
              />
            )}
            {!originalDisplay && (
              // syncWithTicks keeps only the gridlines that have a YAxis tick.
              // Without it recharts adds two unlabelled lines flush with the top
              // and bottom of the plot area, which read as a border rather than
              // as grid. The bottom edge is still drawn by the XAxis axis line.
              <CartesianGrid vertical={false} stroke={theme.global_border} syncWithTicks />
            )}
            <Area
              type="linear"
              dataKey="disadvantage"
              stroke={disadvantageColor}
              fillOpacity={0.4}
              fill={disadvantageColor}
              activeDot={{
                strokeWidth: 0,
                r: 3
              }}
            />
            <Area
              type="linear"
              dataKey="advantage"
              stroke={advantageColor}
              fillOpacity={0.4}
              fill={advantageColor}
              activeDot={{
                strokeWidth: 0,
                r: 3
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

GoldAdvantageChart.displayName = 'GoldAdvantageChart';

export default GoldAdvantageChart;
